import { styled } from "@pigment-css/react";
import { ChevronRightThin } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import type { Note } from "@acto/taskpane/core/domain";
import { RowBody, RowChevron } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { DateBadge, RecapRowItem } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapList";

const Row = styled(RecapRowItem)({
  alignItems: "flex-start",
});

const Body = styled(RowBody)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[8],
}));

const Summary = styled("p")(({ theme }) => ({
  margin: 0,
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 3,
  overflow: "hidden",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const OpenTodos = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  color: theme.fieldSalesTwo.colors.charts.primary,
}));

const Chevron = styled(RowChevron)(({ theme }) => ({
  alignSelf: "flex-start",
  marginTop: theme.fieldSalesTwo.spacing[16],
}));

interface ReportRowProps {
  recap: Note;
  onClick: () => void;
}

export function ReportRow({ recap, onClick }: ReportRowProps) {
  const t = useTranslations("fieldSalesTwo.recaps.customer");

  const summary = recap.summary?.trim() || recap.title?.trim() || recap.content.trim();

  return (
    <Row
      clickable
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <DateBadge>
        <span>{recap.createdAt.format("D")}</span>
        <span>{recap.createdAt.format("MMM").toUpperCase()}</span>
      </DateBadge>
      <Body>
        <Summary>{summary}</Summary>
        {recap.openTodosCount > 0 && <OpenTodos>{t("openTodos", { count: recap.openTodosCount })}</OpenTodos>}
      </Body>
      <Chevron>
        <ChevronRightThin strokeWidth={1.5} />
      </Chevron>
    </Row>
  );
}
